
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router';
import {
  ArrowLeft,
  Loader2,
  Mail,
  Sparkles,
  Info,
  Check,
  Zap,
  RefreshCw,
  LayoutGrid,
  List,
  Send,
  ChevronDown,
} from 'lucide-react';
import { apiPost, apiGet, apiPatch } from '@/lib/api';
import { useToast } from '@/hooks/useToast';
import { useExtension } from '@/hooks/useExtension';
import { sendDraftRequest, sendBatchStart, sendBatchEnd } from '@/lib/extension-bridge';
import { getCampaign, getCampaignLeads, updateCampaign } from '@/lib/campaigns';
import { AIProgressBar } from '@/components/AIProgressBar';
import { BatchSummaryModal } from '@/components/BatchSummaryModal';
import { TONE_PRESETS } from '@hypema/shared';
import type {
  Lead,
  EmailGenerateResponse,
  DraftPayload,
  TonePreset,
} from '@hypema/shared';

/* ── Types ── */

interface EmailData extends EmailGenerateResponse {
  draftStatus: 'none' | 'creating' | 'created' | 'failed' | 'skipped' | 'sent';
}

interface LeadEmailEntry {
  lead: Lead;
  email: EmailData | null;
  editedSubject: string | null;
  editedBody: string | null;
  isRegenerating: boolean;
}

/* ── Helpers ── */

function getSubject(entry: LeadEmailEntry): string {
  return entry.editedSubject ?? entry.email?.subject ?? '';
}

function getBody(entry: LeadEmailEntry): string {
  return entry.editedBody ?? entry.email?.body ?? '';
}

function leadName(lead: Lead): string {
  return [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email;
}


/* ── View Toggle ── */

function ViewToggle({ mode, onChange }: { mode: 'grid' | 'list'; onChange: (m: 'grid' | 'list') => void }) {
  return (
    <div className="flex items-center rounded-lg bg-base-300/30 p-0.5">
      <button
        onClick={() => onChange('list')}
        className={`p-1.5 rounded-md transition-all ${
          mode === 'list' ? 'bg-primary text-primary-content' : 'text-base-content/40 hover:text-base-content/60'
        }`}
        title="List view"
      >
        <List size={14} />
      </button>
      <button
        onClick={() => onChange('grid')}
        className={`p-1.5 rounded-md transition-all ${
          mode === 'grid' ? 'bg-primary text-primary-content' : 'text-base-content/40 hover:text-base-content/60'
        }`}
        title="Grid view"
      >
        <LayoutGrid size={14} />
      </button>
    </div>
  );
}

/* ── Email Editor (shared expanded section) ── */

function EmailEditor({
  entry,
  onSubjectChange,
  onBodyChange,
  onCreateDraft,
}: {
  entry: LeadEmailEntry;
  onSubjectChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  onCreateDraft: () => void;
}) {
  if (!entry.email) {
    return (
      <div className="px-4 pb-4 border-t border-white/5 pt-3">
        <p className="text-xs text-base-content/30 text-center py-2">
          No email generated yet. Select this lead and click Generate.
        </p>
      </div>
    );
  }

  return (
    <div className="pl-11 pr-4 pb-4 border-t border-white/5 pt-3 space-y-3 animate-fade-in">
      <div>
        <label className="text-[10px] text-base-content/30 mb-1 block">Subject</label>
        <input
          value={getSubject(entry)}
          onChange={(e) => onSubjectChange(e.target.value)}
          className="input input-sm input-bordered w-full text-sm"
          placeholder="Subject"
        />
      </div>
      <div>
        <label className="text-[10px] text-base-content/30 mb-1 block">Body</label>
        <textarea
          value={getBody(entry)}
          onChange={(e) => onBodyChange(e.target.value)}
          onInput={(e) => {
            const t = e.target as HTMLTextAreaElement;
            t.style.height = 'auto';
            t.style.height = t.scrollHeight + 'px';
          }}
          className="textarea textarea-bordered w-full text-sm leading-relaxed min-h-[240px]"
        />
      </div>
      <div className="flex justify-end">
        {entry.email.draftStatus === 'created' ? (
          <span className="badge badge-sm badge-success gap-1"><Check size={10} /> Drafted</span>
        ) : entry.email.draftStatus === 'creating' ? (
          <Loader2 size={14} className="animate-spin text-primary" />
        ) : (
          <button onClick={onCreateDraft} className="btn btn-primary btn-xs gap-1">
            <Mail size={10} /> Create Draft
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Grid Card ── */

function LeadGridCard({
  entry,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  onSubjectChange,
  onBodyChange,
  onCreateDraft,
  onRegenerate,
}: {
  entry: LeadEmailEntry;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onSubjectChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  onCreateDraft: () => void;
  onRegenerate: () => void;
}) {
  const name = leadName(entry.lead);
  const hasEmail = entry.email !== null;
  const subject = getSubject(entry);

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="p-3.5 cursor-pointer" onClick={onToggleExpand}>
        <div className="flex items-center gap-2.5">
          <input
            type="checkbox"
            className="checkbox checkbox-sm checkbox-primary"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{name}</p>
            <p className="text-xs text-base-content/40 truncate">
              {entry.lead.company}{entry.lead.roleTitle && ` · ${entry.lead.roleTitle}`}
            </p>
          </div>
        </div>
        {hasEmail && !isExpanded && (
          <div className="flex items-center gap-2 mt-2 pl-7">
            <Mail size={11} className="text-base-content/25 shrink-0" />
            <p className="text-xs text-base-content/45 truncate flex-1">{subject}</p>
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
              className="btn btn-ghost btn-xs btn-circle"
              title="Regenerate"
            >
              <RefreshCw size={11} className={entry.isRegenerating ? 'animate-spin text-primary' : 'text-base-content/30'} />
            </button>
          </div>
        )}
      </div>
      {isExpanded && (
        <EmailEditor
          entry={entry}
          onSubjectChange={onSubjectChange}
          onBodyChange={onBodyChange}
          onCreateDraft={onCreateDraft}
        />
      )}
    </div>
  );
}

/* ── List Row ── */

function LeadListRow({
  entry,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  onSubjectChange,
  onBodyChange,
  onCreateDraft,
  onRegenerate,
}: {
  entry: LeadEmailEntry;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onSubjectChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  onCreateDraft: () => void;
  onRegenerate: () => void;
}) {
  const name = leadName(entry.lead);
  const hasEmail = entry.email !== null;
  const subject = getSubject(entry);

  return (
    <div>
      <div
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-base-200/30 cursor-pointer transition-colors"
        onClick={onToggleExpand}
      >
        <div className="w-7 shrink-0 flex items-center justify-center">
          <input
            type="checkbox"
            className="checkbox checkbox-sm checkbox-primary"
            checked={isSelected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(); }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div className="w-36 min-w-0 truncate text-sm">{name}</div>
        <div className="w-28 min-w-0 truncate text-xs text-base-content/60">{entry.lead.company || '—'}</div>
        <div className="w-44 min-w-0 truncate text-xs text-base-content/50">{entry.lead.email}</div>
        <div className="flex-1 min-w-0 truncate text-sm text-base-content/70">
          {hasEmail ? subject : <span className="text-base-content/25">—</span>}
        </div>
        <div className="w-16 shrink-0 flex items-center justify-center">
          {entry.email?.draftStatus === 'sent' ? (
            <span className="flex items-center gap-1 text-[10px] text-green-500 font-medium">
              <Check size={10} /> Sent
            </span>
          ) : entry.email?.draftStatus === 'skipped' ? (
            <span className="text-[10px] text-amber-500 font-medium">Skipped</span>
          ) : entry.email?.draftStatus === 'failed' ? (
            <span className="text-[10px] text-red-500 font-medium">Failed</span>
          ) : entry.email?.draftStatus === 'creating' ? (
            <span className="text-[10px] text-primary font-medium">Sending...</span>
          ) : (
            <span className="text-[10px] text-base-content/20">—</span>
          )}
        </div>
        <div className="w-7 shrink-0 flex items-center justify-center">
          {hasEmail && (
            <button
              onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
              className="btn btn-ghost btn-xs btn-circle"
              title="Regenerate"
            >
              <RefreshCw size={11} className={entry.isRegenerating ? 'animate-spin text-primary' : 'text-base-content/30'} />
            </button>
          )}
        </div>
      </div>
      {/* Body preview hidden in list view — click row to expand */}
      {isExpanded && (
        <EmailEditor
          entry={entry}
          onSubjectChange={onSubjectChange}
          onBodyChange={onBodyChange}
          onCreateDraft={onCreateDraft}
        />
      )}
    </div>
  );
}

/* ── Main Page ── */

export function CampaignReviewPage() {
  const params = useParams();
  const campaignId = params.id as string;
  const toast = useToast();
  const { connected } = useExtension();

  const [entries, setEntries] = useState<Record<string, LeadEmailEntry>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    if (typeof window === 'undefined') return 'list';
    return (localStorage.getItem('review_view_mode') as 'grid' | 'list') || 'list';
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ done: 0, total: 0, sent: 0, skipped: 0, failed: 0 });
  const cancelRef = useRef(false);
  const [batchSummary, setBatchSummary] = useState<{ created: number; skipped: number; failed: number; total: number } | null>(null);
  const [secretSauce, setSecretSauce] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [toneOpen, setToneOpen] = useState(false);
  const toneRef = useRef<HTMLDivElement>(null);

  const [campaign, setCampaign] = useState({ name: '', tone: 'professional', context: '', product: '' });

  // Close tone dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (toneRef.current && !toneRef.current.contains(e.target as Node)) setToneOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleToneChange(tone: string) {
    setCampaign((prev) => ({ ...prev, tone }));
    updateCampaign(campaignId, { tone }).catch(() => toast.error('Failed to save tone'));
    setToneOpen(false);
  }

  const availableTones = TONE_PRESETS.filter((t) => t !== 'casual');

  const loadRef = useRef<() => void>(undefined);

  // Auto-refresh when returning from Gmail (tab visibility change)
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'visible' && !sending && loadRef.current) {
        loadRef.current();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [sending]);

  useEffect(() => {
    async function load() {
      try {
        const [campaignData, campaignLeads] = await Promise.all([
          getCampaign(campaignId),
          getCampaignLeads(campaignId),
        ]);
        setCampaign({
          name: campaignData.name,
          tone: campaignData.tone,
          context: campaignData.context || '',
          product: campaignData.product || '',
        });
        const leads = campaignLeads as Lead[];
        const record: Record<string, LeadEmailEntry> = {};
        for (const lead of leads) {
          record[lead.id] = { lead, email: null, editedSubject: null, editedBody: null, isRegenerating: false };
        }

        // Load previously generated emails from database
        if (leads.length > 0) {
          try {
            const leadIds = leads.map((l) => l.id).join(',');
            const existingEmails = await apiGet<Record<string, { id: string; subject: string; body: string; unsubscribeUrl: string }>>(
              `/emails/by-leads?leadIds=${leadIds}`
            );
            for (const [leadId, emailData] of Object.entries(existingEmails)) {
              if (record[leadId]) {
                // If the lead's status is 'sent' in the database, mark as already sent
                const leadStatus = record[leadId].lead.status;
                record[leadId].email = {
                  id: emailData.id,
                  subject: emailData.subject,
                  body: emailData.body,
                  unsubscribeUrl: emailData.unsubscribeUrl,
                  draftStatus: leadStatus === 'sent' ? 'sent' : 'none',
                };
              }
            }
          } catch {
            // Silently ignore — emails just won't be pre-loaded
          }
        }

        setEntries(record);
        // Pre-deselect leads that have already been sent
        setSelected(new Set(leads.filter((l) => l.status !== 'sent').map((l) => l.id)));
      } catch {}
      setLoading(false);
    }
    loadRef.current = load;
    load();
  }, [campaignId]);

  function handleViewModeChange(m: 'grid' | 'list') {
    setViewMode(m);
    localStorage.setItem('review_view_mode', m);
  }

  const updateEntry = useCallback((leadId: string, patch: Partial<LeadEmailEntry>) => {
    setEntries((prev) => ({
      ...prev,
      [leadId]: { ...prev[leadId], ...patch },
    }));
  }, []);

  const allLeads = Object.values(entries);
  const selectedCount = selected.size;
  const totalCount = allLeads.length;
  const hasAnyEmails = allLeads.some((e) => e.email !== null);
  const selectedWithEmails = allLeads.filter((e) => selected.has(e.lead.id) && e.email !== null);
  const selectedWithoutEmails = allLeads.filter((e) => selected.has(e.lead.id) && e.email === null);
  const sendableCount = allLeads.filter((e) => selected.has(e.lead.id) && e.email && e.email.draftStatus !== 'created').length;

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === totalCount) setSelected(new Set());
    else setSelected(new Set(allLeads.map((e) => e.lead.id)));
  }

  function buildKnowledgeSnippet(): string | undefined {
    const parts: string[] = [];
    if (secretSauce) parts.push('ACTIVATE_SECRET_SAUCE');
    if (aiInstructions.trim()) parts.push(aiInstructions.trim());
    return parts.length ? parts.join('\n\n') : undefined;
  }

  const snippetLength = (secretSauce ? 'ACTIVATE_SECRET_SAUCE\n\n'.length : 0) + aiInstructions.length;
  const snippetRemaining = 3000 - snippetLength;

  async function generateForLeads(leadIds: string[]) {
    if (leadIds.length === 0) return;
    setGenerating(true);
    let count = 0;

    for (const leadId of leadIds) {
      const entry = entries[leadId];
      if (!entry) continue;
      try {
        const email = await apiPost<EmailGenerateResponse>('/emails/generate', {
          leadId,
          context: campaign.context,
          tone: campaign.tone as TonePreset,
          knowledgeSnippet: buildKnowledgeSnippet(),
          campaignId,
        });
        updateEntry(leadId, {
          email: { ...email, draftStatus: 'none' },
          editedSubject: null,
          editedBody: null,
          isRegenerating: false,
        });
        count++;
      } catch (err: any) {
        toast.error(`Failed for ${entry.lead.email}: ${err.message}`);
      }
    }

    setGenerating(false);
    if (leadIds.length > 0) setExpandedId(leadIds[0]);
    if (count > 0) toast.success(`Generated ${count} emails`);
  }

  function handleGenerate() {
    const ids = selectedWithoutEmails.map((e) => e.lead.id);
    if (ids.length === 0) {
      toast.warning('Select leads without emails to generate');
      return;
    }
    generateForLeads(ids);
  }

  function handleRegenerateAll() {
    const ids = allLeads.filter((e) => selected.has(e.lead.id)).map((e) => e.lead.id);
    if (ids.length === 0) {
      toast.warning('Select at least one lead');
      return;
    }
    generateForLeads(ids);
  }

  async function handleRegenerateSingle(leadId: string) {
    updateEntry(leadId, { isRegenerating: true });
    try {
      const email = await apiPost<EmailGenerateResponse>('/emails/generate', {
        leadId,
        context: campaign.context,
        tone: campaign.tone as TonePreset,
        knowledgeSnippet: buildKnowledgeSnippet(),
      });
      updateEntry(leadId, {
        email: { ...email, draftStatus: 'none' },
        editedSubject: null,
        editedBody: null,
        isRegenerating: false,
      });
      toast.success('Email regenerated');
    } catch (err: any) {
      updateEntry(leadId, { isRegenerating: false });
      toast.error(err.message || 'Regeneration failed');
    }
  }

  async function handleCreateDraft(leadId: string) {
    const entry = entries[leadId];
    if (!entry?.email) return;

    if (!connected) {
      toast.error('Chrome extension not detected. Install it to create drafts.');
      return;
    }

    updateEntry(leadId, { email: { ...entry.email, draftStatus: 'creating' } });

    try {
      const hasEdits = entry.editedSubject !== null || entry.editedBody !== null;
      let payload: DraftPayload;

      if (hasEdits) {
        payload = {
          provider: 'gmail',
          to: entry.lead.email,
          subject: getSubject(entry),
          body: getBody(entry),
        };
      } else {
        payload = await apiPost<DraftPayload>('/emails/drafts/payload', {
          leadId: entry.lead.id,
          generatedEmailId: entry.email.id,
        });
      }

      const result = await sendDraftRequest(payload);
      if (result.success) {
        if (result.sent) {
          updateEntry(leadId, { email: { ...entry.email, draftStatus: 'sent' } });
          // Persist sent status to Supabase
          apiPatch(`/leads/${leadId}`, { status: 'sent' }).catch(() => toast.error('Failed to update sent status'));
          toast.success(`Email sent to ${entry.lead.email}`);
        } else {
          updateEntry(leadId, { email: { ...entry.email, draftStatus: 'skipped' } });
          toast.info(`Skipped ${entry.lead.email}`);
        }
      } else {
        updateEntry(leadId, { email: { ...entry.email, draftStatus: 'failed' } });
        toast.error(result.error || 'Failed to create draft');
      }
    } catch (err: any) {
      updateEntry(leadId, { email: { ...entry.email, draftStatus: 'failed' } });
      toast.error(err.message || 'Draft creation failed');
    }
  }

  async function handleSkip(leadId: string) {
    const entry = entries[leadId];
    if (!entry?.email) return;
    updateEntry(leadId, { email: { ...entry.email, draftStatus: 'skipped' } });
  }

  async function handleSendAll() {
    if (!connected) {
      toast.error('Chrome extension not detected. Install it to create drafts.');
      return;
    }

    const sendable = allLeads.filter(
      (e) => selected.has(e.lead.id) && e.email && e.email.draftStatus !== 'created' && e.email.draftStatus !== 'skipped' && e.email.draftStatus !== 'sent'
    );
    if (sendable.length === 0) {
      toast.warning('No selected emails to send');
      return;
    }

    cancelRef.current = false;
    setSending(true);
    setSendProgress({ done: 0, total: sendable.length, sent: 0, skipped: 0, failed: 0 });
    sendBatchStart(sendable.length);

    let sent = 0;
    let skipped = 0;
    let failed = 0;

    for (let i = 0; i < sendable.length; i++) {
      // Check cancel
      if (cancelRef.current) {
        for (let j = i; j < sendable.length; j++) {
          skipped++;
          updateEntry(sendable[j].lead.id, { email: { ...sendable[j].email!, draftStatus: 'skipped' } });
        }
        break;
      }

      const entry = sendable[i];

      try {
        const hasEdits = entry.editedSubject !== null || entry.editedBody !== null;
        let payload: DraftPayload;

        if (hasEdits) {
          payload = {
            provider: 'gmail',
            to: entry.lead.email,
            subject: getSubject(entry),
            body: getBody(entry),
          };
        } else {
          payload = await apiPost<DraftPayload>('/emails/drafts/payload', {
            leadId: entry.lead.id,
            generatedEmailId: entry.email!.id,
          });
        }

        updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'creating' } });
        const result = await sendDraftRequest(payload);

        if (result.success) {
          if (result.sent) {
            sent++;
            updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'sent' } });
            apiPatch(`/leads/${entry.lead.id}`, { status: 'sent' }).catch(() => toast.error('Failed to update sent status'));
          } else {
            skipped++;
            updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'skipped' } });
          }
        } else {
          if (result.error?.includes('cancelled') || result.error?.includes('Batch')) {
            skipped++;
            updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'skipped' } });
            for (let j = i + 1; j < sendable.length; j++) {
              skipped++;
              updateEntry(sendable[j].lead.id, { email: { ...sendable[j].email!, draftStatus: 'skipped' } });
            }
            break;
          }
          failed++;
          updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'failed' } });
        }
      } catch (err: any) {
        failed++;
        updateEntry(entry.lead.id, { email: { ...entry.email!, draftStatus: 'failed' } });
      }

      setSendProgress({ done: i + 1, total: sendable.length, sent, skipped, failed });
    }

    sendBatchEnd({ created: sent, skipped, failed });
    setSending(false);
    setBatchSummary({ created: sent, skipped, failed, total: sendable.length });
  }

  function toggleExpand(id: string) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  /* ── Render ── */

  return (
    <div className="space-y-6 max-w-7xl mx-auto w-full animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold tracking-tight">Review & Draft</h1>
        <p className="text-sm text-base-content/40 mt-1">
          Generate emails, refine with AI, then create Gmail drafts.
        </p>
      </div>

      {/* Done banner — shows when all leads have been processed */}
      {(() => {
        const sentCount = allLeads.filter((e) => e.email?.draftStatus === 'sent').length;
        const skippedCount = allLeads.filter((e) => e.email?.draftStatus === 'skipped').length;
        const doneCount = sentCount + skippedCount;
        if (doneCount > 0 && doneCount >= allLeads.length && !sending) {
          return (
            <div className="glass-card rounded-xl p-4 border border-green-500/20 bg-green-500/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Check size={20} className="text-green-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-green-600">Done</p>
                  <p className="text-xs text-base-content/50">
                    {sentCount} email{sentCount !== 1 ? 's' : ''} sent
                    {skippedCount > 0 ? `, ${skippedCount} skipped` : ''}
                  </p>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Toolbar */}
      <div className="glass-card rounded-xl overflow-hidden">
        {/* Top bar: select all + view toggle */}
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm checkbox-primary"
              checked={selectedCount === totalCount && totalCount > 0}
              onChange={toggleAll}
            />
            Select all ({selectedCount}/{totalCount})
          </label>
          <div className="flex items-center gap-3">
            <ViewToggle mode={viewMode} onChange={handleViewModeChange} />
            {hasAnyEmails && (
              <button
                onClick={handleSendAll}
                disabled={sendableCount === 0 || sending || generating}
                className="btn btn-primary btn-sm gap-1.5"
              >
                {sending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending {sendProgress.done}/{sendProgress.total}
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Send All ({sendableCount})
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="p-3 border-b border-white/5">
          <div className="flex items-center gap-3">
            {/* Left: Secret Sauce */}
            <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-all duration-300 shrink-0 ${
              secretSauce ? 'glass-card secret-sauce-glow' : ''
            }`}>
              <input type="checkbox" checked={secretSauce}
                onChange={(e) => setSecretSauce(e.target.checked)} className="hidden" />
              <div className={`w-10 h-5 rounded-full transition-all duration-300 flex items-center px-0.5 ${
                secretSauce ? 'bg-gradient-to-r from-violet-500 via-pink-500 to-amber-500' : 'bg-base-300/50'
              }`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-300 ${
                  secretSauce ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </div>
              <span className="text-sm font-medium flex items-center gap-1.5 select-none">
                <Zap size={14} className={`transition-colors duration-300 ${secretSauce ? 'text-amber-400' : 'text-base-content/30'}`} />
                Secret Sauce
              </span>
              <div className="relative group">
                <Info size={12} className="text-base-content/30" />
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-base-content text-base-100 text-xs w-56 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-150 z-50">
                  Activates the master sales knowledge base. Emails will be rewritten using proven sales frameworks.
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-base-content" />
                </div>
              </div>
            </label>

            <div className="flex-1" />

            {/* Tone selector — centered */}
            <div className="relative shrink-0" ref={toneRef}>
              <button
                onClick={() => setToneOpen(!toneOpen)}
                className="inline-flex items-center gap-1.5 text-sm cursor-pointer"
              >
                <span className="text-base-content/40">Tone:</span>
                <span className="font-medium text-primary capitalize">{campaign.tone}</span>
                <ChevronDown size={10} className={`text-primary transition-transform ${toneOpen ? 'rotate-180' : ''}`} />
              </button>
              {toneOpen && (
                <div className="absolute top-full left-0 mt-1 py-1 rounded-lg bg-base-200 border border-base-300/30 shadow-lg z-50 min-w-[140px] animate-fade-in">
                  {availableTones.map((t) => (
                    <button
                      key={t}
                      onClick={() => handleToneChange(t)}
                      className={`w-full px-3 py-1.5 text-left text-sm capitalize transition-colors ${
                        campaign.tone === t
                          ? 'text-primary font-medium'
                          : 'text-base-content/60 hover:text-base-content hover:bg-base-300/20'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1" />

            {/* Right: AI input + action button */}
            {hasAnyEmails ? (
              <div className="flex items-center gap-1.5 shrink-0">
                <input
                  type="text"
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  placeholder="What to change..."
                  className="input input-bordered input-sm w-44 text-xs"
                />
                <button
                  onClick={handleRegenerateAll}
                  disabled={selectedCount === 0 || generating}
                  className={`btn btn-sm gap-1 ${
                    aiInstructions.trim() ? 'btn-primary' : 'btn-ghost'
                  }`}
                >
                  {generating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={13} />}
                  Regenerate
                </button>
              </div>
            ) : (
              <button
                onClick={handleGenerate}
                disabled={selectedCount === 0 || generating}
                className={`btn btn-primary btn-sm gap-1 shrink-0 ${secretSauce ? 'secret-sauce-glow' : ''}`}
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Generate ({selectedCount})
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {generating && (
          <div className="px-3 py-2 border-b border-white/5">
            <AIProgressBar
              active={generating}
              size="sm"
              steps={[
                'Analyzing lead profiles...',
                'Crafting personalized subject lines...',
                'Writing email body for each lead...',
                'Applying tone and style...',
                'Finalizing email drafts...',
              ]}
            />
          </div>
        )}

        {/* Lead list */}
        {loading ? (
          <div className="p-6 space-y-2">
            {[...Array(5)].map((_, i) => <div key={i} className="h-8 animate-shimmer rounded" />)}
          </div>
        ) : totalCount === 0 ? (
          <div className="p-8 text-center text-base-content/40 text-sm">
            No leads found. Go back and import or generate some leads first.
          </div>
        ) : viewMode === 'grid' ? (
          <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {allLeads.map((entry) => (
              <LeadGridCard
                key={entry.lead.id}
                entry={entry}
                isSelected={selected.has(entry.lead.id)}
                isExpanded={expandedId === entry.lead.id}
                onToggleSelect={() => toggleSelect(entry.lead.id)}
                onToggleExpand={() => toggleExpand(entry.lead.id)}
                onSubjectChange={(v) => updateEntry(entry.lead.id, { editedSubject: v })}
                onBodyChange={(v) => updateEntry(entry.lead.id, { editedBody: v })}
                onCreateDraft={() => handleCreateDraft(entry.lead.id)}
                onRegenerate={() => handleRegenerateSingle(entry.lead.id)}
              />
            ))}
          </div>
        ) : (
          <div>
            {/* Column headers */}
            <div className="flex items-center gap-3 px-4 py-2 border-b border-white/5 text-[10px] font-semibold uppercase tracking-wider text-base-content/30">
              <div className="w-7" />
              <div className="w-36">Name</div>
              <div className="w-28">Company</div>
              <div className="w-44">Email</div>
              <div className="flex-1">Subject</div>
              <div className="w-16 text-center">Status</div>
              <div className="w-7" />
            </div>
            <div className="divide-y divide-white/5">
              {allLeads.map((entry) => (
                <LeadListRow
                  key={entry.lead.id}
                  entry={entry}
                  isSelected={selected.has(entry.lead.id)}
                  isExpanded={expandedId === entry.lead.id}
                  onToggleSelect={() => toggleSelect(entry.lead.id)}
                  onToggleExpand={() => toggleExpand(entry.lead.id)}
                  onSubjectChange={(v) => updateEntry(entry.lead.id, { editedSubject: v })}
                  onBodyChange={(v) => updateEntry(entry.lead.id, { editedBody: v })}
                  onCreateDraft={() => handleCreateDraft(entry.lead.id)}
                  onRegenerate={() => handleRegenerateSingle(entry.lead.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        <Link to={`/campaign/${campaignId}/leads`} className="btn btn-ghost gap-1">
          <ArrowLeft size={14} /> Leads
        </Link>
      </div>

      {/* Sending overlay */}
      {sending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-base-100/95" />
          <div className="relative bg-base-100 border border-base-300/30 rounded-2xl p-6 w-full max-w-sm mx-4 space-y-5 animate-fade-in shadow-2xl">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
                <Send size={24} className="text-primary animate-pulse" />
              </div>
              <h2 className="text-lg font-bold">Sending Emails</h2>
              <p className="text-sm text-base-content/40">
                Switch to Gmail to review and send each draft.
              </p>
            </div>

            <div className="space-y-2">
              <div className="h-2 bg-base-content/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${sendProgress.total > 0 ? (sendProgress.done / sendProgress.total) * 100 : 0}%` }}
                />
              </div>
              <p className="text-sm font-semibold text-primary text-center">
                {sendProgress.done}/{sendProgress.total}
              </p>
            </div>

            <div className="space-y-2">
              {sendProgress.sent > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-success/5 border border-success/10">
                  <Check size={16} className="text-success shrink-0" />
                  <span className="text-sm font-medium flex-1">Sent</span>
                  <span className="text-sm font-bold text-success">{sendProgress.sent}</span>
                </div>
              )}
              {sendProgress.skipped > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/5 border border-warning/10">
                  <Info size={16} className="text-warning shrink-0" />
                  <span className="text-sm font-medium flex-1">Skipped</span>
                  <span className="text-sm font-bold text-warning">{sendProgress.skipped}</span>
                </div>
              )}
              {sendProgress.failed > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-error/5 border border-error/10">
                  <Info size={16} className="text-error shrink-0" />
                  <span className="text-sm font-medium flex-1">Failed</span>
                  <span className="text-sm font-bold text-error">{sendProgress.failed}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                cancelRef.current = true;
                sendBatchEnd({ created: sendProgress.sent, skipped: sendProgress.skipped, failed: sendProgress.failed });
                setSending(false);
                setBatchSummary({ created: sendProgress.sent, skipped: sendProgress.skipped + (sendProgress.total - sendProgress.done), failed: sendProgress.failed, total: sendProgress.total });
              }}
              className="btn btn-ghost btn-sm btn-block text-base-content/40 hover:text-error"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Batch summary modal */}
      <BatchSummaryModal
        open={!!batchSummary}
        created={batchSummary?.created ?? 0}
        skipped={batchSummary?.skipped ?? 0}
        failed={batchSummary?.failed ?? 0}
        total={batchSummary?.total ?? 0}
        onClose={() => setBatchSummary(null)}
      />
    </div>
  );
}
